'use client';

import { useState, useEffect } from 'react';

export type Lang = 'en' | 'cs';

export function useLanguage() {
  const [lang, setLang] = useState<Lang>('en');

  useEffect(() => {
    const stored = localStorage.getItem('vq_lang') as Lang | null;
    if (stored === 'en' || stored === 'cs') setLang(stored);
  }, []);

  const toggleLang = () => {
    const next: Lang = lang === 'en' ? 'cs' : 'en';
    setLang(next);
    localStorage.setItem('vq_lang', next);
  };

  return { lang, toggleLang };
}

export const translations = {
  en: {
    // Common
    vqstudio: 'Loyalo',
    appSub: 'Digital Loyalty · Est. 2024',
    staffLabel: 'Loyalo Staff',
    cancel: 'Cancel',
    remove: 'Remove',
    logOut: 'Log Out',
    logOutTitle: 'Log out of Loyalo?',
    logOutDescCustomer: "You\'ll need to sign in again to view your loyalty card.",
    logOutDescStaff: "You\'ll need to sign in again to access staff features.",
    session: 'Session',

    // Customer Home
    welcomeBack: 'Welcome back',
    hiName: (name: string) => `Hi, ${name} ♡`,
    yourLoyaltyQR: 'Your Loyalty QR',
    tapToBrighten: 'Tap to brighten · Show to your staff',
    yourLoyaltyProgress: 'Your Loyalty Progress',
    card: 'Card',
    allRewardsUnlocked: 'All rewards unlocked ♡',
    cardStarted: 'Card started',
    yourRewards: 'Your Rewards',
    moreVisits: (n: number, milestone: string) => `${n} more ${n === 1 ? 'visit' : 'visits'} → ${milestone} OFF`,

    // Customer History
    myHistory: 'My History',
    totalVisits: 'Total Visits',
    cardsDone: 'Cards Done',
    rewardsEarned: 'Rewards Earned',
    currentCard: 'Current Card',
    completedCards: 'Completed Cards',
    loyaltyCard: 'Loyalty Card',
    completed: 'Completed',
    started: 'Started',
    active: 'Active',
    rewards: 'Rewards',
    sessions: 'Sessions',
    notEarned: 'Not earned',
    available: 'Available ♡',
    redeemed: 'Redeemed',
    noHistoryYet: 'No history yet',
    noHistoryDesc: 'Your loyalty card sessions will appear here after your first visit.',

    // Customer Profile
    myProfile: 'My Profile',
    loyaltyMember: 'Loyalty Member',
    account: 'Account',
    fullName: 'Full name',
    email: 'Email',
    notSet: 'Not set',
    language: 'Language',
    languageDesc: 'App language',
    appSubProfile: 'Digital Loyalty · Est. 2024',

    // Seller Scan
    vqstudioStaff: 'Loyalo Staff',
    scanCustomer: 'Scan Customer',
    readyToScan: 'Ready to scan',
    askCustomer: 'Ask the customer to open their loyalty QR',
    qrVerified: 'QR Verified ✓',
    loadingCustomer: 'Loading customer...',
    scanFailed: 'Scan Failed',
    pointCamera: 'Point camera at customer\'s QR code',
    scanCustomerQR: 'SCAN CUSTOMER QR',
    recentScans: 'Recent Scans',
    sessionAdded: 'Session added',
    pending: 'Pending',

    // Seller Customers
    customers: 'Customers',
    searchPlaceholder: 'Search by name or ID...',
    name: 'Name',
    progress: 'Progress',
    rewardsLabel: 'Rewards',
    total: 'total',
    noCustomersFound: 'No customers found',
    noCustomersDesc: "Try a different name or customer ID to find who you're looking for.",
    removeCustomer: 'Remove Customer?',
    removeCustomerDesc: (name: string) => `Remove ${name} from your customer list? Their loyalty history will be lost.`,
    lastVisit: 'Last visit',
    completedCount: 'completed',

    // Seller Dashboard
    vqstudioBusiness: 'Loyalo Business',
    dashboard: 'Dashboard',
    sessionsToday: 'Sessions Today',
    activeCustomers: 'Active Customers',
    rewardsPending: 'Rewards Pending',
    needsAttention: 'Needs attention',
    cardsCompleted: 'Cards Completed',
    searchStaff: 'Search customer or staff...',
    all: 'All',
    sessionsTab: 'Sessions',
    rewardsTab: 'Rewards',
    completedTab: 'Completed',
    activityLog: 'Activity Log',
    entries: 'entries',
    noActivityMatch: 'No activity matches your search',

    // Seller Settings
    settings: 'Settings',
    signedInAs: 'Signed in as',
    staffAccount: 'Staff account',
    appearance: 'Appearance',
    darkMode: 'Dark Mode',
    darkActive: 'Dark theme active',
    lightActive: 'Light theme active',

    // Verification
    qrScanResult: 'QR Scan Result',
    verification: 'Verification',
    qrCodeVerified: '✓ QR CODE VERIFIED',
    currentProgress: 'Current Progress',
    moreVisitsToComplete: (n: number) => `${n} more ${n === 1 ? 'visit' : 'visits'} to complete this card`,
    cardCompleteTitle: 'Loyalty Card Complete! 🎉',
    cardCompleteDesc: 'Starting new card automatically...',
    addSession: 'ADD SESSION',
    addingSession: 'ADDING...',
    redeemReward: (d: string) => `REDEEM ${d} OFF`,
    confirmRedeem: (d: string) => `Redeem ${d} OFF?`,
    confirmRedeemDesc: (d: string) => `This will mark the ${d} OFF reward as used. This cannot be undone.`,
    confirmRedeemBtn: (d: string) => `Redeem ${d}`,
    newCard: 'NEW CARD',
    locked: 'Locked',
  },
  cs: {
    // Common
    vqstudio: 'Loyalo',
    appSub: 'Digitální věrnostní program · Est. 2024',
    staffLabel: 'Loyalo Personál',
    cancel: 'Zrušit',
    remove: 'Odebrat',
    logOut: 'Odhlásit se',
    logOutTitle: 'Odhlásit se z Loyalo?',
    logOutDescCustomer: 'Pro zobrazení věrnostní karty se budete muset znovu přihlásit.',
    logOutDescStaff: 'Pro přístup k funkcím personálu se budete muset znovu přihlásit.',
    session: 'Relace',

    // Customer Home
    welcomeBack: 'Vítejte zpět',
    hiName: (name: string) => `Ahoj, ${name} ♡`,
    yourLoyaltyQR: 'Váš věrnostní QR kód',
    tapToBrighten: 'Klepněte pro jas · Ukažte personálu',
    yourLoyaltyProgress: 'Váš věrnostní postup',
    card: 'Karta',
    allRewardsUnlocked: 'Všechny odměny odemčeny ♡',
    cardStarted: 'Karta zahájena',
    yourRewards: 'Vaše odměny',
    moreVisits: (n: number, milestone: string) => `Ještě ${n} ${n === 1 ? 'návštěva' : 'návštěvy'} → ${milestone} SLEVA`,

    // Customer History
    myHistory: 'Moje historie',
    totalVisits: 'Celkem návštěv',
    cardsDone: 'Dokončené karty',
    rewardsEarned: 'Získané odměny',
    currentCard: 'Aktuální karta',
    completedCards: 'Dokončené karty',
    loyaltyCard: 'Věrnostní karta',
    completed: 'Dokončeno',
    started: 'Zahájeno',
    active: 'Aktivní',
    rewards: 'Odměny',
    sessions: 'Návštěvy',
    notEarned: 'Nezískaná',
    available: 'Dostupná ♡',
    redeemed: 'Uplatněna',
    noHistoryYet: 'Zatím žádná historie',
    noHistoryDesc: 'Vaše návštěvy věrnostní karty se zobrazí po první návštěvě.',

    // Customer Profile
    myProfile: 'Můj profil',
    loyaltyMember: 'Věrnostní člen',
    account: 'Účet',
    fullName: 'Celé jméno',
    email: 'E-mail',
    notSet: 'Nenastaveno',
    language: 'Jazyk',
    languageDesc: 'Jazyk aplikace',
    appSubProfile: 'Digitální věrnostní program · Est. 2024',

    // Seller Scan
    vqstudioStaff: 'Loyalo Personál',
    scanCustomer: 'Skenovat zákazníka',
    readyToScan: 'Připraveno ke skenování',
    askCustomer: 'Požádejte zákazníka, aby otevřel věrnostní QR kód',
    qrVerified: 'QR ověřen ✓',
    loadingCustomer: 'Načítání zákazníka...',
    scanFailed: 'Skenování selhalo',
    pointCamera: 'Namiřte kameru na QR kód zákazníka',
    scanCustomerQR: 'SKENOVAT QR ZÁKAZNÍKA',
    recentScans: 'Nedávná skenování',
    sessionAdded: 'Návštěva přidána',
    pending: 'Čeká',

    // Seller Customers
    customers: 'Zákazníci',
    searchPlaceholder: 'Hledat podle jména nebo ID...',
    name: 'Jméno',
    progress: 'Postup',
    rewardsLabel: 'Odměny',
    total: 'celkem',
    noCustomersFound: 'Žádní zákazníci nenalezeni',
    noCustomersDesc: 'Zkuste jiné jméno nebo ID zákazníka.',
    removeCustomer: 'Odebrat zákazníka?',
    removeCustomerDesc: (name: string) => `Odebrat ${name} ze seznamu zákazníků? Jejich věrnostní historie bude ztracena.`,
    lastVisit: 'Poslední návštěva',
    completedCount: 'dokončeno',

    // Seller Dashboard
    vqstudioBusiness: 'Loyalo Obchod',
    dashboard: 'Přehled',
    sessionsToday: 'Návštěvy dnes',
    activeCustomers: 'Aktivní zákazníci',
    rewardsPending: 'Čekající odměny',
    needsAttention: 'Vyžaduje pozornost',
    cardsCompleted: 'Dokončené karty',
    searchStaff: 'Hledat zákazníka nebo personál...',
    all: 'Vše',
    sessionsTab: 'Návštěvy',
    rewardsTab: 'Odměny',
    completedTab: 'Dokončeno',
    activityLog: 'Protokol aktivit',
    entries: 'záznamů',
    noActivityMatch: 'Žádná aktivita neodpovídá vašemu hledání',

    // Seller Settings
    settings: 'Nastavení',
    signedInAs: 'Přihlášen jako',
    staffAccount: 'Účet personálu',
    appearance: 'Vzhled',
    darkMode: 'Tmavý režim',
    darkActive: 'Tmavý motiv aktivní',
    lightActive: 'Světlý motiv aktivní',

    // Verification
    qrScanResult: 'Výsledek QR skenování',
    verification: 'Ověření',
    qrCodeVerified: '✓ QR KÓD OVĚŘEN',
    currentProgress: 'Aktuální postup',
    moreVisitsToComplete: (n: number) => `Ještě ${n} ${n === 1 ? 'návštěva' : 'návštěvy'} pro dokončení karty`,
    cardCompleteTitle: 'Věrnostní karta dokončena! 🎉',
    cardCompleteDesc: 'Automaticky se spouští nová karta...',
    addSession: 'PŘIDAT NÁVŠTĚVU',
    addingSession: 'PŘIDÁVÁNÍ...',
    redeemReward: (d: string) => `UPLATNIT ${d} SLEVU`,
    confirmRedeem: (d: string) => `Uplatnit ${d} slevu?`,
    confirmRedeemDesc: (d: string) => `Tím se označí odměna ${d} jako použitá. Tuto akci nelze vrátit.`,
    confirmRedeemBtn: (d: string) => `Uplatnit ${d}`,
    newCard: 'NOVÁ KARTA',
    locked: 'Uzamčeno',
  },
};
